import { useQuery } from '@tanstack/react-query';
import { useCustomInstance } from '@/lib/use-custom-instance';
import type { KycAccessTokenResponse, KycStatusResponse } from './types';

// Interim data layer over the Orval mutator, mirroring src/lib/documents/useDocuments.ts.
// Replace with the generated `useKycController*` hooks once the api is reachable
// and `yarn generate-api` has been run.

export const kycStatusQueryKey = () => ['kyc', 'status'] as const;
export const kycAccessTokenQueryKey = (flowKey: string) => ['kyc', 'access-token', flowKey] as const;

/**
 * The current user's KYC verifications and statuses.
 *
 * Pass `refetchInterval` (ms) to poll — the KYC gate uses this to watch for the
 * status flipping to APPROVED while it waits on Sumsub's webhook to land.
 */
export function useKycStatus(enabled: boolean, refetchInterval?: number) {
  const request = useCustomInstance<KycStatusResponse>();
  return useQuery({
    queryKey: kycStatusQueryKey(),
    queryFn: () => request({ url: '/kyc/status', method: 'GET' }),
    enabled,
    staleTime: 60_000,
    refetchInterval: refetchInterval ?? false,
  });
}

/**
 * Mint a Sumsub WebSDK access token for a flow. Modelled as a query (not a
 * mutation) so it auto-loads on open and the WebSDK's expiration handler can
 * re-mint via `refetch()` — both without a mount effect that would trip the
 * cascading-render lint. The token is short-lived, so it is never cached
 * (`staleTime`/`gcTime` 0) and failures are surfaced rather than retried.
 */
export function useKycAccessToken(flowKey: string | null) {
  const request = useCustomInstance<KycAccessTokenResponse>();
  return useQuery({
    queryKey: kycAccessTokenQueryKey(flowKey ?? ''),
    queryFn: () => request({ url: '/kyc/access-token', method: 'POST', data: { flowKey } }),
    enabled: !!flowKey,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export const kycActionTokenQueryKey = (externalActionId: string) => ['kyc', 'action-token', externalActionId] as const;

/**
 * Mint a Sumsub WebSDK token to complete one of the user's pending action checks
 * (e.g. an admin-triggered one). Same query-not-mutation shape as
 * `useKycAccessToken` so the WebSDK's expiration handler can re-mint via refetch.
 */
export function useKycActionToken(externalActionId: string | null) {
  const request = useCustomInstance<KycAccessTokenResponse>();
  return useQuery({
    queryKey: kycActionTokenQueryKey(externalActionId ?? ''),
    queryFn: () => request({ url: `/kyc/actions/${externalActionId}/token`, method: 'POST' }),
    enabled: !!externalActionId,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}
